
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "./use-toast";
import { Department, DepartmentDetail } from "@/types/department";
import { TaskAssignee } from "@/types/kanban";

export const useDepartments = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setIsLoading(true);
        
        // Fetch departments
        const { data: departmentsData, error: departmentsError } = await supabase
          .from('departments')
          .select('*');
        
        if (departmentsError) throw departmentsError;
        
        // For each department, fetch the count of MFOs
        const departmentsWithCounts = await Promise.all(
          departmentsData.map(async (dept) => {
            const { count, error: countError } = await supabase
              .from('mfo_items')
              .select('*', { count: 'exact', head: true })
              .eq('department_id', dept.id);
              
            if (countError) {
              console.error("Error fetching MFO count:", countError);
              return { 
                ...dept, 
                mfoCount: 0,
                collaborators: [] as TaskAssignee[]
              } as unknown as Department;
            }
            
            // Fetch a few employees to show as collaborators
            const { data: employees, error: empError } = await supabase
              .from('employees')
              .select('id, name, avatar')
              .eq('department_id', dept.id)
              .limit(5);
            
            // Map database fields to our Department type
            return {
              id: dept.id,
              name: dept.name,
              description: dept.description || "",
              headName: dept.head_name,
              headTitle: dept.head_title,
              headAvatar: dept.head_avatar,
              budget: Number(dept.budget),
              year: dept.year,
              mfoCount: count || 0,
              collaborators: empError ? [] : employees.map(emp => ({
                id: emp.id,
                name: emp.name,
                avatar: emp.avatar
              }))
            } as Department;
          })
        );
        
        setDepartments(departmentsWithCounts);
      } catch (error: any) {
        setError(error.message);
        toast({
          title: "Failed to load departments",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDepartments();
  }, [toast]);

  const getDepartmentDetail = async (id: string): Promise<DepartmentDetail | null> => {
    try {
      // Fetch department
      const { data: department, error: deptError } = await supabase
        .from('departments')
        .select('*')
        .eq('id', id)
        .single();
      
      if (deptError) throw deptError;
      
      // Fetch department goals
      const { data: goals, error: goalsError } = await supabase
        .from('department_goals')
        .select('goal')
        .eq('department_id', id);
      
      if (goalsError) throw goalsError;
      
      // Fetch employees for department
      const { data: employees, error: empError } = await supabase
        .from('employees')
        .select('id, name, avatar')
        .eq('department_id', id);
        
      if (empError) throw empError;
      
      // Get MFO count
      const { count: mfoCount, error: countError } = await supabase
        .from('mfo_items')
        .select('*', { count: 'exact', head: true })
        .eq('department_id', id);
      
      if (countError) throw countError;
      
      // Map database fields to our DepartmentDetail type
      return {
        id: department.id,
        name: department.name,
        description: department.description || "",
        headName: department.head_name,
        headTitle: department.head_title,
        headAvatar: department.head_avatar,
        budget: Number(department.budget),
        year: department.year,
        mfoCount: mfoCount || 0,
        vision: department.vision || "",
        mission: department.mission || "",
        goals: goals.map(g => g.goal),
        collaborators: employees.map(emp => ({
          id: emp.id,
          name: emp.name,
          avatar: emp.avatar
        }))
      };
    } catch (error: any) {
      toast({
        title: "Failed to load department details",
        description: error.message,
        variant: "destructive"
      });
      return null;
    }
  };

  return { departments, isLoading, error, getDepartmentDetail };
};
